import { Map as MapGL, NavigationControl } from "react-map-gl/mapbox";
import React, { useMemo, useRef } from "react";

import { MapWidgetConfiguration, PublishedDashboard } from "@/lib/api";
import { useDashboardData } from "@/features/shared/hooks";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { useRefreshAction } from "@/features/dashboard/features/refresh-timer/hooks";
import { useMapLayers } from "@/lib/map-layers";
import MapControlPositioner from "@/components/map/map-control-positioner";
import { accountOwnedMapStyleUrl } from "@/lib/mapbox/base-layer-styles";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";

import { MapWidgetLayerFactory } from "./layer-factory";
import { MapStack } from "./map.styles";
import LayerControl from "./controls/layer-control";
import Player from "./controls/player-control";
import MapContextProvider from "./settings/map-settings-context";
import useMapViewState from "./hooks/useMapViewState";

import "mapbox-gl/dist/mapbox-gl.css";
import "./map.css";

type MapProps = {
  configuration: MapWidgetConfiguration;
};
type MapInnerProps = MapProps & {
  selectedDashboard: PublishedDashboard;
};

const Map = (props: MapProps) => {
  const { selectedDashboard } = useDashboardData();
  if (!selectedDashboard) return null;

  return <MapInner {...props} selectedDashboard={selectedDashboard} />;
};

const MapInner = ({ configuration, selectedDashboard }: MapInnerProps) => {
  const selectedTimeRange = useSelectedTimeRange();

  const { base_layer_coords } = configuration;
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const { mapViewState } = useMapViewState({
    containerRef: mapContainerRef,
    initialCoords: base_layer_coords,
  });

  const layerConfigs = useMemo(() => {
    const layerFactory = new MapWidgetLayerFactory(selectedDashboard);
    return configuration.layers.map((mapWidgetLayerConfig) => ({
      layer: layerFactory.createLayer(mapWidgetLayerConfig),
      isEnabledInitially: mapWidgetLayerConfig.enabled_by_default,
    }));
  }, [configuration, selectedDashboard]);

  const mapLayers = useMapLayers({ layerConfigs, timeRange: selectedTimeRange });

  useRefreshAction(mapLayers.refresh);

  const mapTileStyles = configuration.map_box_style_id ?? import.meta.env.VITE_MAP_STYLE_ID;
  const mapStyleUrl = accountOwnedMapStyleUrl(mapTileStyles);
  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;

  return (
    <MapContextProvider>
      <MapStack direction="column" height="100%" width="100%" ref={mapContainerRef}>
        {mapViewState && (
          <MapGL
            initialViewState={mapViewState}
            attributionControl={false}
            projection={{ name: "mercator" }}
            mapStyle={mapStyleUrl}
            mapboxAccessToken={mapTileAccessToken}
            maxPitch={0}
            bearingSnap={360}
            bearing={0}
          >
            <DeckGLOverlay
              interleaved
              controller
              layers={mapLayers.layers.map((it) => it.renderedLayer)}
            />

            {mapLayers.layers.map((it) => (
              <React.Fragment key={it.id}>{it.renderedComponents}</React.Fragment>
            ))}

            <NavigationControl showCompass={false} position="bottom-right" showZoom />
            <MapControlPositioner position="top-left">
              <LayerControl mapLayersController={mapLayers} />
            </MapControlPositioner>
            <Player {...mapLayers} />
          </MapGL>
        )}
      </MapStack>
    </MapContextProvider>
  );
};

export default Map;
